<template>
  <div class="admin-payments-view">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h4 font-weight-bold">Payment Management</h1>
    </div>

    <div class="payment-tabs d-flex justify-space-between align-end">
      <v-tabs v-model="activeTab" color="#e94057">
        <v-tab value="all">ALL PAYMENTS</v-tab>
        <v-tab value="pending">PENDING</v-tab>
        <v-tab value="review">UNDER REVIEW</v-tab>
        <v-tab value="approved">APPROVED</v-tab>
        <v-tab value="paid">PAID</v-tab>
        <v-tab value="rejected">REJECTED</v-tab>
      </v-tabs>
      <div class="d-flex align-center gap-6 payment-stats-container">
        <div class="payment-stat">
          <div class="stat-label">Paid</div>
          <div class="stat-value">${{ formatAmount(totalPaidAmount) }}</div>
        </div>
        <div class="payment-stat">
          <div class="stat-label">Under Review</div>
          <div class="stat-value">${{ formatAmount(totalUnderReviewAmount) }}</div>
        </div>
        <div class="payment-stat">
          <div class="stat-label">Pending</div>
          <div class="stat-value">${{ formatAmount(totalPendingAmount) }}</div>
        </div>
      </div>
    </div>
    
    <div class="payment-search mb-4 d-flex align-center">
      <v-text-field
        v-model="searchTerm"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search Payments by User"
        single-line
        hide-details
        variant="outlined"
        density="comfortable"
        @input="onSearchChange"
        bg-color="white"
        class="search-field flex-grow-1"
      ></v-text-field>
      <ActionButton
        variant="secondary"
        left-icon="mdi-download"
        :loading="downloadingCsv"
        class="ml-4"
        @click="downloadPaymentsCsv"
      >
        Download CSV
      </ActionButton>
    </div>
    
    <div v-if="loading" class="payments-table">
      <v-card class="payments-table-card mb-4 elevation-0">
        <div class="skeleton-table">
          <!-- Skeleton Table Header -->
          <div class="skeleton-header">
            <div v-for="header in tableHeaders" :key="header.value" class="skeleton-header-cell">
              <div v-if="header.text" class="skeleton-box skeleton-text" style="width: 60%;"></div>
            </div>
          </div>
          
          <!-- Skeleton Table Rows -->
          <div v-for="i in 7" :key="i" class="skeleton-row">
            <!-- User Column -->
            <div class="skeleton-cell" style="flex: 1.5;">
              <div class="skeleton-user-info">
                <div class="skeleton-box skeleton-text" style="width: 75%; margin-bottom: 6px;"></div>
                <div class="skeleton-box skeleton-text-small" style="width: 85%;"></div>
              </div>
            </div>
            <!-- Amount Column -->
            <div class="skeleton-cell" style="flex: 1;">
              <div class="skeleton-box skeleton-text" style="width: 60%;"></div>
            </div>
            <!-- Assessment Column -->
            <div class="skeleton-cell" style="flex: 1;">
              <div class="skeleton-box skeleton-text" style="width: 70%;"></div>
            </div>
            <!-- Date Column -->
            <div class="skeleton-cell" style="flex: 1;">
              <div class="skeleton-box skeleton-text" style="width: 65%;"></div>
            </div>
            <!-- Status Column -->
            <div class="skeleton-cell" style="flex: 1; justify-content: center;">
              <div class="skeleton-box skeleton-chip"></div>
            </div>
            <!-- Actions Column -->
            <div class="skeleton-cell" style="flex: 1; justify-content: flex-end;">
              <div class="skeleton-actions">
                <div class="skeleton-box skeleton-button"></div>
                <div class="skeleton-box skeleton-button"></div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </div>
    
    <div v-else-if="error" class="payments-error">
      <p>{{ error }}</p>
      <v-btn @click="fetchPayments" color="primary">Try Again</v-btn>
    </div>
    
    <div v-else-if="displayedPayments.length === 0" class="payments-empty">
      <p>No payments found.</p>
    </div>
    
    <div v-else class="payments-table">
      <v-card class="payments-table-card mb-4 elevation-0">
        <DataTable
          :headers="tableHeaders"
          :items="displayedPayments"
          :loading="loading"
          :show-pagination="false"
          class="payments-data-table"
        >
          <!-- User column -->
          <template #column-user="{ item }">
            <div class="user-info">
              <div class="font-weight-medium">{{ item.user_first_name }} {{ item.user_last_name }}</div>
              <div class="text-caption text-grey">{{ item.user_email }}</div>
            </div>
          </template>
          
          <!-- Amount column -->
          <template #column-amount="{ item }">
            <div class="font-weight-medium">${{ item.amount }} {{ item.currency }}</div>
          </template>
          
          <!-- Assessment column -->
          <template #column-assessment="{ item }">
            <div v-if="item.assessment_title">{{ item.assessment_title }}</div>
            <div v-else class="text-grey">N/A</div>
          </template>
          
          <!-- Date column -->
          <template #column-created_at="{ item }">
            <div>{{ formatDate(item.created_at) }}</div>
          </template>
          
          <!-- Status column -->
          <template #column-status="{ item }">
            <div class="d-flex justify-center">
              <v-chip
                :color="getStatusColor(item.status)"
                :text-color="getStatusTextColor(item.status)"
                class="status-chip"
                size="small"
              >
                {{ getStatusText(item.status) }}
              </v-chip>
            </div>
          </template>
          
          <!-- Actions column -->
          <template #column-actions="{ item }">
            <div class="d-flex justify-end gap-4">
              <v-menu open-on-hover offset-y>
                <template #activator="{ props, on }">
                  <ActionButton v-bind="props" v-on="on" variant="secondary">Actions</ActionButton>
                </template>
                <v-list>
                  <v-list-item @click="markAsPaid(item)" :disabled="item.status === 'paid'">
                    <v-list-item-title>Execute Payment</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="updatePaymentStatus(item, 'approved')" :disabled="item.status === 'paid' || item.status === 'approved'">
                    <v-list-item-title>Approve</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="updatePaymentStatus(item, 'rejected')" :disabled="item.status === 'paid' || item.status === 'rejected'">
                    <v-list-item-title>Reject</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="editPaymentNotes(item)">
                    <v-list-item-title>Edit Notes</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              
              <!-- Primary action button based on status -->
              <ActionButton 
                v-if="item.status === 'pending'" 
                variant="primary" 
                @click="updatePaymentStatus(item, 'approved')"
              >
                Approve
              </ActionButton>
              
              <ActionButton 
                v-else-if="item.status === 'approved' || item.status === 'review'" 
                variant="primary" 
                @click="markAsPaid(item)"
                :loading="processingPayment === item.id"
              >
                Execute Payment
              </ActionButton>
            </div>
          </template>
        </DataTable>
      </v-card>
    </div>
    
    <!-- Edit Notes Dialog -->
    <v-dialog v-model="showNotesDialog" max-width="500">
      <v-card>
        <v-card-title>Edit Payment Notes</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="editingNotes"
            label="Notes"
            rows="4"
            variant="outlined"
            placeholder="Add notes about this payment..."
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" @click="closeNotesDialog">Cancel</v-btn>
          <v-btn color="#e94057" @click="savePaymentNotes">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="showConfirmDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">{{ confirmAction.title }}</v-card-title>
        <v-card-text>
          {{ confirmAction.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" @click="showConfirmDialog = false">Cancel</v-btn>
          <v-btn :color="confirmAction.color" variant="text" @click="executeConfirmAction">{{ confirmAction.button }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminPaymentsView',
  components: {
    DataTable,
    ActionButton
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      activeTab: 'all',
      searchTerm: '',
      searchDebounce: null,
      loading: false,
      error: null,
      payments: [],
      processingPayment: null,
      downloadingCsv: false,
      // Global metrics from backend
      metrics: {
        total_paid_amount: 0,
        total_under_review_amount: 0,
        total_pending_amount: 0,
      },
      
      // Table configuration
      tableHeaders: [
        { text: 'User', value: 'user', align: 'start', sortable: true },
        { text: 'Amount', value: 'amount', align: 'start', sortable: true },
        { text: 'Assessment', value: 'assessment', align: 'start', sortable: false },
        { text: 'Date', value: 'created_at', align: 'start', sortable: true },
        { text: 'Status', value: 'status', align: 'center', sortable: true },
        { text: '', value: 'actions', align: 'end', sortable: false },
      ],
      
      // Dialog states
      showNotesDialog: false,
      editingPayment: null,
      editingNotes: '',
      
      showConfirmDialog: false,
      confirmAction: {
        title: '',
        message: '',
        color: 'primary',
        button: 'Confirm',
        callback: null
      }
    };
  },
  computed: {
    displayedPayments() {
      let filtered = [...this.payments];
      
      // Filter by status
      if (this.activeTab !== 'all') {
        filtered = filtered.filter(payment => payment.status === this.activeTab);
      }
      
      // Filter by search term
      if (this.searchTerm.trim()) {
        const search = this.searchTerm.toLowerCase();
        filtered = filtered.filter(payment => 
          (payment.user_first_name || '').toLowerCase().includes(search) ||
          (payment.user_last_name || '').toLowerCase().includes(search) ||
          (payment.user_email || '').toLowerCase().includes(search)
        );
      }
      
      return filtered;
    },
    
    totalPaidAmount() {
      return this.metrics.total_paid_amount || 0;
    },
    
    totalUnderReviewAmount() {
      return this.metrics.total_under_review_amount || 0;
    },
    
    totalPendingAmount() {
      return this.metrics.total_pending_amount || 0;
    }
  },
  methods: {
    async fetchPayments() {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await apiService.get('/business/admin/payouts/');
        // Backend returns full list plus global metrics
        this.payments = response.data.results || response.data || [];
        if (response.data.metrics) {
          this.metrics = response.data.metrics;
        }
        
      } catch (error) {
        console.error('Error fetching payments:', error);
        this.error = 'Failed to load payments. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    async markAsPaid(payment) {
      this.confirmAction = {
        title: 'Mark Payment as Paid',
        message: `Are you sure you want to mark this payment of $${payment.amount} ${payment.currency} to ${payment.user_first_name} ${payment.user_last_name} as paid? This will trigger the Google Sheets integration.`,
        color: '#e94057',
        button: 'Execute Payment',
        callback: () => this.executeMarkAsPaid(payment)
      };
      this.showConfirmDialog = true;
    },
    
    async executeMarkAsPaid(payment) {
      try {
        this.processingPayment = payment.id;
        this.showConfirmDialog = false;
        
        const response = await apiService.post(`/business/admin/payouts/${payment.id}/mark_paid/`);
        
        this.toast.success(response.data.detail || 'Payment marked as paid successfully');
        
        // Refresh the payments list
        await this.fetchPayments();
        
      } catch (error) {
        console.error('Error marking payment as paid:', error);
        
        let errorMessage = 'Failed to mark payment as paid';
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
        
        this.toast.error(errorMessage);
      } finally {
        this.processingPayment = null;
      }
    },
    
    async updatePaymentStatus(payment, newStatus) {
      this.confirmAction = {
        title: `${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)} Payment`,
        message: `Are you sure you want to ${newStatus} this payment?`,
        color: newStatus === 'rejected' ? 'red' : '#e94057',
        button: newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
        callback: () => this.executeStatusUpdate(payment, newStatus)
      };
      this.showConfirmDialog = true;
    },
    
    async executeStatusUpdate(payment, newStatus) {
      try {
        this.showConfirmDialog = false;
        
        const response = await apiService.patch(`/business/admin/payouts/${payment.id}/`, {
          status: newStatus
        });
        
        this.toast.success(`Payment ${newStatus} successfully`);
        
        // Update the payment in the local list
        const index = this.payments.findIndex(p => p.id === payment.id);
        if (index !== -1) {
          this.payments[index] = { ...this.payments[index], ...response.data };
        }
        
      } catch (error) {
        console.error('Error updating payment status:', error);
        this.toast.error('Failed to update payment status');
      }
    },
    
    editPaymentNotes(payment) {
      this.editingPayment = payment;
      this.editingNotes = payment.notes || '';
      this.showNotesDialog = true;
    },
    
    closeNotesDialog() {
      this.showNotesDialog = false;
      this.editingPayment = null;
      this.editingNotes = '';
    },
    
    async savePaymentNotes() {
      if (!this.editingPayment) return;
      
      try {
        const response = await apiService.patch(`/business/admin/payouts/${this.editingPayment.id}/`, {
          notes: this.editingNotes
        });
        
        this.toast.success('Payment notes updated successfully');
        
        // Update the payment in the local list
        const index = this.payments.findIndex(p => p.id === this.editingPayment.id);
        if (index !== -1) {
          this.payments[index] = { ...this.payments[index], ...response.data };
        }
        
        this.closeNotesDialog();
        
      } catch (error) {
        console.error('Error updating payment notes:', error);
        this.toast.error('Failed to update payment notes');
      }
    },
    
    executeConfirmAction() {
      if (this.confirmAction.callback) {
        this.confirmAction.callback();
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`;
    },
    
    formatAmount(amount) {
      if (!amount) return '0.00';
      return Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    getStatusText(status) {
      const statusMap = {
        'pending': 'Pending',
        'review': 'Under Review',
        'approved': 'Approved',
        'paid': 'Paid',
        'rejected': 'Rejected'
      };
      return statusMap[status] || status;
    },
    
    getStatusColor(status) {
      const colorMap = {
        'pending': 'orange',
        'review': 'blue',
        'approved': 'green',
        'paid': 'success',
        'rejected': 'red'
      };
      return colorMap[status] || 'grey';
    },
    
    getStatusTextColor(status) {
      return 'white';
    },
    
    onSearchChange() {
      // Client-side filtering only; no network requests needed
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = setTimeout(() => {}, 200);
    },

    async downloadPaymentsCsv() {
      try {
        this.downloadingCsv = true;
        const response = await apiService.get('/business/admin/payouts/export_csv/', {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const dateStamp = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.setAttribute('download', `expert_payments_${dateStamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading payments CSV:', error);
        this.toast.error('Failed to download CSV. Please try again.');
      } finally {
        this.downloadingCsv = false;
      }
    }
  },
  watch: {
    // No backend refetch needed; filtering is client-side now
    activeTab() {}
  },
  created() {
    this.fetchPayments();
  }
};
</script>

<style scoped>
.admin-payments-view {
  padding: 24px;
  background-color: #f8f9fa;
}

.payment-tabs {
  margin-bottom: 20px;
}

.payment-stats-container {
  margin-bottom: 12px;
}

.payment-search {
  margin-bottom: 20px;
}

.search-field {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.payments-error, .payments-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.payments-error p, .payments-empty p {
  margin: 20px 0;
  color: #666;
}

.status-chip {
  min-width: 80px;
  justify-content: center;
}

.payments-table-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.payments-data-table {
  width: 100%;
  padding: 0 16px;
}

.user-info {
  display: flex;
  flex-direction: column;
}

/* Table styling similar to AssessmentsView */
.payments-data-table :deep(table) {
  background-color: #f8f9fa;
  border-collapse: separate;
  border-spacing: 0 8px;
}

.payments-data-table :deep(tr) {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;
}

.payments-data-table :deep(tr:hover) {
  background-color: #f8f9fa;
}

.payments-data-table :deep(th) {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.payments-data-table :deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.payment-stat {
  text-align: center;
  min-width: 120px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* Skeleton Loader Styles */
.skeleton-table {
  padding: 16px;
  background-color: #f8f9fa;
}

.skeleton-header {
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-header-cell {
  flex: 1;
  display: flex;
  align-items: center;
}

.skeleton-row {
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  align-items: center;
}

.skeleton-cell {
  display: flex;
  align-items: center;
}

.skeleton-user-info {
  width: 100%;
}

.skeleton-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.skeleton-box {
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 20%,
    #f0f0f0 40%,
    #e0e0e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 16px;
}

.skeleton-text-small {
  height: 12px;
}

.skeleton-chip {
  height: 24px;
  width: 80px;
  border-radius: 12px;
}

.skeleton-button {
  height: 36px;
  width: 90px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style> 